import { isModernPricing } from "~/utils/pricing"

// The Nov 23 price change for members still on legacy pricing. Every piece of site-wide messaging
// about it (top banner, pop-up, the blocks on the subscribe and bundle pages, the "Yearly" callout)
// goes through showPriceChangeMessaging() below, so the audience and the end date live in one place.

// Midnight at the start of Nov 23 in New York. EST (-05:00): daylight saving ends on Nov 1.
export const PRICE_CHANGE_CUTOFF = new Date("2026-11-23T00:00:00-05:00")

export const PRICE_ANNOUNCEMENT_PATH = "/price-announcement"

// Logged-in members on legacy pricing ("control" and null cohorts), until the cutoff. Logged-out
// visitors and every modern cohort (test, v3, v4) already see the new prices, so there is nothing
// to announce to them. `now` is injectable for tests.
export function showPriceChangeMessaging(user: unknown, now: Date = new Date()): boolean {
  return !!user && !isModernPricing(user) && now < PRICE_CHANGE_CUTOFF
}

// Pages the pop-up stays off: the announcement itself, the pages that already carry the
// price-change block, sign-in and onboarding, and anywhere a member is part-way through paying.
// Prefixes, so sub-paths are covered too.
const PRICE_POPUP_EXCLUDED_PATHS = [
  PRICE_ANNOUNCEMENT_PATH,
  "/subscribe",
  "/getting-started",
  "/birthday-bundle",
  "/cindy-bundle",
  "/welcome-bundle",
  "/login",
  "/register",
  "/signup",
  "/users/sign_in",
  "/users/sign_up",
  "/forgot-password",
  "/reset-password",
  "/change-password",
  "/confirm",
  "/resend-confirmation",
  "/join",
  "/billing",
  "/cards",
  "/redeem",
  "/gift-subscription",
  "/annual-subscription-promo",
]

export function isPricePopupExcludedPath(pathname: string): boolean {
  return PRICE_POPUP_EXCLUDED_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`))
}

// The one bar at the top of the site, which shrinks as the member engages:
//   full    — the two-line banner (phones get the compact bar instead; see PriceChangeBanner)
//   compact — the one-line "Prices change on 11/23 – learn more" bar, with an X: they've read the
//             announcement or dismissed the pop-up
//   off     — nothing: they X'd the compact bar, or messaging is off
export type PriceBannerState = "full" | "compact" | "off"

// What the root loader hands the client, decided on the server.
export type PriceChangeFlags = {
  // Banner, subscribe/bundle blocks and the "Yearly" callout.
  messaging: boolean
  // The pop-up: messaging is on AND this member hasn't read the announcement or dismissed the pop-up.
  popup: boolean
  // The top bar: "off" whenever messaging is.
  banner: PriceBannerState
}

export const NO_PRICE_CHANGE: PriceChangeFlags = { messaging: false, popup: false, banner: "off" }

// Both X's (the pop-up's and the compact bar's) post here from a fetcher; `dismiss` says which.
export const PRICE_DISMISS_ACTION = "/api/price-announcement-dismiss"
export const PRICE_BAR_DISMISS = "bar"

// Client-side: is the price-change pop-up open on this page view? Set by PriceChangePopup, read by
// the survey pop-up so the two never stack. Lives here, not in either component, to avoid an import
// cycle (the price pop-up borrows the survey pop-up's wordmark).
let pricePopupOpen = false
export const isPriceChangePopupOpen = () => pricePopupOpen
export const setPriceChangePopupOpen = (open: boolean) => {
  pricePopupOpen = open
}
